import { useEffect, useContext, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ModalContext } from 'components/Modals/ModalContext'
import { WorkoutOverrideModalContext } from 'components/Modals/Popups/WorkoutOverrideModal'
import { updateWorkoutTemplateOverrideWeightRecommendationStorage } from 'redux/slices/athleteProfile'
import { WhichWeightAPI } from 'WhichWeightAPI'

import NumberInput from 'components/Input/NumberInput'

export default function WeightRecommendationOverrideInput({ 
    phaseVolumeData, 
    programTableColumnTaperData, 
    phaseIndex, 
    slotIndex, 
    exercise,
    setUnits
}) {

    const dispatch = useDispatch()

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const workoutTemplateOverrideWeightRecommendationStorage = useSelector(state => state.athleteProfile.workoutTemplateOverrideWeightRecommendationStorage)
    const originalWeightRecommendation = workoutTemplateOverrideWeightRecommendationStorage[phaseIndex][slotIndex]

    const { modalData } = useContext(ModalContext)
    const { allowEditing, HandleOverrideChange, GetWorkoutTemplateDisplayData } = useContext(WorkoutOverrideModalContext)

    const weightRecommendationDisplayData = GetWorkoutTemplateDisplayData('one-off-weight-recommendation-override', phaseIndex, slotIndex, exercise.id)

    const requestIdRef = useRef(0)

    const LoadWeightRecommendation = async () => {

        // Increment request ID for this new request
        requestIdRef.current += 1
        const currentRequestId = requestIdRef.current

        try {

            const emptyPayload = {
                phaseIndex: parseInt(phaseIndex),
                slotIndex: parseInt(slotIndex),
                value: null
            }

            dispatch(updateWorkoutTemplateOverrideWeightRecommendationStorage(emptyPayload))

            // Check for phase-level taper data

            const hasPhaseTaperData = (
                phaseVolumeData 
                && phaseVolumeData.taperData 
                && phaseVolumeData.taperData.source === 'phase-load-adjustment-override' 
                && phaseVolumeData.taperData.overrideApplied
            )
            
            // Use phase-level taper data if available, otherwise fall back to program-level

            const taperData = hasPhaseTaperData ? phaseVolumeData.taperData : programTableColumnTaperData

            var jsonBody = null
            
            if (profileType === 'group') {
                jsonBody = {
                    groupId: groupId,
                    weekOfTheYearKey: modalData.weekOfTheYearKey,
                    workoutTemplateKey: modalData.workoutTemplateKey,
                    phaseIndex: parseInt(phaseIndex),
                    slotIndex: parseInt(slotIndex),
                    exerciseId: exercise.id,
                    reps: setUnits,
                    taper: taperData.useTaper ? 1 : 0,
                    taperDampener: taperData.taperDampener
                }
            }

            if (profileType === 'group-individual' || profileType === 'individual') {
                jsonBody = {
                    userId: athleteId,
                    weekOfTheYearKey: modalData.weekOfTheYearKey,
                    workoutTemplateKey: modalData.workoutTemplateKey,
                    phaseIndex: parseInt(phaseIndex),
                    slotIndex: parseInt(slotIndex),
                    exerciseId: exercise.id,
                    reps: setUnits,
                    taper: taperData.useTaper ? 1 : 0,
                    taperDampener: taperData.taperDampener
                }
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('PROGRAM', 'GET', '/weight_recommendation', jsonBody)

            // console.log(result)

            // Only update state if this is still the latest request
            if (currentRequestId === requestIdRef.current) {        
                
                const recommendationPayload = {
                    phaseIndex: parseInt(phaseIndex),
                    slotIndex: parseInt(slotIndex),
                    value: result.data.data.weightRecommendationRounded
                }

                dispatch(updateWorkoutTemplateOverrideWeightRecommendationStorage(recommendationPayload))

            }

        } catch (error) {
            // Only update state if this is still the latest request
            if (currentRequestId === requestIdRef.current) {
                console.error('Error loading weight recommendation:', error)
            }
            return 0
        }
    }

    const HandleWeightRecommendationChange = (value) => {

        const payload = {
            exerciseId: exercise.id,
            weightRecommendation: value
        }

        HandleOverrideChange(
            'one-off-weight-recommendation-override',
            phaseIndex,
            slotIndex,
            payload
        )
    }

    useEffect(() => {
        if (
            exercise !== null
            && exercise.exerciseType === 'recommendation' 
            && exercise.setUnit === 'reps'
            && setUnits !== null
        ) {
            LoadWeightRecommendation()
        }   
    }, [phaseVolumeData, phaseIndex, exercise, setUnits])

    if (exercise.exerciseType === 'bodyweight') {
        return (
            <div className='col-span-1 px-2 py-1 flex items-center justify-center text-sm text-gray-600'>
                Bodyweight
            </div>
        )
    }

    if (exercise.exerciseType === 'recommendation' && originalWeightRecommendation === null) {
        return (
            <div className='col-span-1 px-2 py-1 flex items-center justify-center'>
                <div className='w-full'>
                    <div className='h-6 w-full bg-gray-200 rounded animate-pulse' />
                    <div className='pt-1 pr-4 -mb-1 text-xs'>Loading...</div>
                </div>
            </div>
        )
    }

    if (exercise.exerciseType === 'recommendation' && originalWeightRecommendation !== null) {
        return (
            <div className='col-span-1 px-2 py-1 flex items-center justify-center'>
                {allowEditing &&
                    <NumberInput
                        value={weightRecommendationDisplayData.value}
                        onChange={(value) => HandleWeightRecommendationChange(parseInt(value))}
                        step={5}
                        min={5}
                        max={1000}
                        className={weightRecommendationDisplayData.overrideApplied ? 'bg-orange-100' : ''}
                        subText='lbs'
                    />
                }

                {!allowEditing &&
                    <div className='w-full border border-gray-400 rounded-lg p-1'>
                        <p>{weightRecommendationDisplayData.value} lbs</p>
                    </div>
                }
            </div>
        )
    }
}